import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { CircleDollarSign, FileText, Timer, Users2, Wallet2 } from "lucide-react";
import Item from "./Item";
import { useScheduleContext } from "../../../../hooks/context/useScheduleContext";
import { useFilters } from "../../../../../../../utils/hooks/useFilters";
import useApi from "../../../../../../../utils/api";
import { useCompanyCurrency } from "../../../../../../../utils/hooks/company";
import { formatTimeSheetParams } from "../../../../../../TimeSheet/api/helpers";
import { currencyFormatter } from "../../../../../../../utils/helpers/currencyFormatter";

const getTrend = (percentage: number) => {
    if (percentage > 0) return "up";
    if (percentage < 0) return "down";
    return "equal";
};

export const renderDuration = (duration: number) => {
    let totalMinutes = Math.floor(duration) / 60;
    if (isNaN(totalMinutes)) return "0m";
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    const hoursPart = hours > 0 ? `${hours}h` : "";
    const minutesPart = minutes > 0 ? `${minutes}m` : "";
    return `${hoursPart} ${minutesPart}`.trim() || "0m";
};

const calculatePercentageChange = (current = 0, last = 0) => {
    if (last === 0 && current === 0) return 0;
    if (last === 0) return 100;
    return (((current - last) / Math.abs(last)) * 100).toFixed(2);
};

function EventsStatistics() {
    const { t } = useTranslation();
    const currency = useCompanyCurrency();
    const [_filters] = useFilters();
    const { dispatch, type } = useScheduleContext();
    const { authGet } = useApi();

    const filters = useMemo(() => {
        return {
            ...formatTimeSheetParams({
                filters: _filters?.filters || [],
                from: _filters?.mode === "month" ? moment(_filters?.from).startOf("week").format("YYYY-MM-DD") : _filters?.from,
                to: _filters?.mode === "month" ? moment(_filters?.to).endOf("week").format("YYYY-MM-DD") : _filters?.to
            }),
            sort: _filters?.sort,
            mode: _filters?.mode
        }
    }, [_filters]);

    const url = useMemo(() => {
        if (type === "home") {
            return "/my/schedules/v2/overview";
        }
        return "/schedules/v2/overview";
    }, [type]);

    const query = useQuery({
        queryKey: [url, filters],
        queryFn: ({ queryKey }: any) => {
            const [url, params] = queryKey;
            return authGet(url, { params });
        }
    });

    const calculatedValues = useMemo(() => {
        const employeesCurrent = query.data?.employees?.current?.withEvents || 0;
        const employeesLast = query.data?.employees?.last?.withEvents || 0;

        const regularCurrent = query.data?.regular?.current || {};
        const regularLast = query.data?.regular?.last || {};

        const overtimeCurrent = query.data?.overtime?.current || {};
        const overtimeLast = query.data?.overtime?.last || {};

        return {
            employees: {
                value: `${employeesCurrent}/${query.data?.employees?.current?.total || 0}`,
                percentage: calculatePercentageChange(employeesCurrent, employeesLast)
            },

            regular: {
                total: currencyFormatter(regularCurrent.total || 0, currency),
                duration: renderDuration(regularCurrent.duration || 0),
                totalPercentage: calculatePercentageChange(
                    regularCurrent.total || 0,
                    regularLast.total || 0
                ),
                durationPercentage: calculatePercentageChange(
                    regularCurrent.duration || 0,
                    regularLast.duration || 0
                )
            },

            overtime: {
                total: currencyFormatter(overtimeCurrent.total || 0, currency),
                duration: renderDuration(overtimeCurrent.duration || 0),
                totalPercentage: calculatePercentageChange(
                    overtimeCurrent.total || 0,
                    overtimeLast.total || 0
                ),
                durationPercentage: calculatePercentageChange(
                    overtimeCurrent.duration || 0,
                    overtimeLast.duration || 0
                )
            }
        };
    }, [query.data, currency]);

    useEffect(() => {
        if (!query.isLoading) {
            dispatch({ type: "refetch-event-statistics", payload: query.refetch });
        }
    }, [query.refetch, dispatch, query.isLoading]);

    return (
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-4 tw-mb-3">
            <Item
                title={t("scheduled-employees")}
                icon={Users2}
                value={calculatedValues.employees.value}
                percentage={`${calculatedValues.employees.percentage}%`}
                trend={getTrend(Number(calculatedValues.employees.percentage))}
                loading={query.isLoading}
            />
            <Item
                title={t("regular-wadges")}
                icon={Wallet2}
                value={calculatedValues.regular.total}
                percentage={`${calculatedValues.regular.totalPercentage}%`}
                trend={getTrend(Number(calculatedValues.regular.totalPercentage))}
                loading={query.isLoading}
            />
            <Item
                title={t("regular-hours")}
                icon={FileText}
                value={calculatedValues.regular.duration}
                percentage={`${calculatedValues.regular.durationPercentage}%`}
                trend={getTrend(Number(calculatedValues.regular.durationPercentage))}
                loading={query.isLoading}
            />
            <Item
                title={t("overtime-cost")}
                icon={CircleDollarSign}
                value={calculatedValues.overtime.total}
                percentage={`${calculatedValues.overtime.totalPercentage}%`}
                trend={getTrend(Number(calculatedValues.overtime.totalPercentage))}
                loading={query.isLoading}
            />
            <Item
                title={t("overtime-hours")}
                icon={Timer}
                value={calculatedValues.overtime.duration}
                percentage={`${calculatedValues.overtime.durationPercentage}%`}
                trend={getTrend(Number(calculatedValues.overtime.durationPercentage))}
                loading={query.isLoading}
            />
        </div>
    );
}

export default EventsStatistics;
